import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import { esAdmin as esCuentaAdmin } from '../utils/roles';

// 17 sep 2026 (Augusto: "debería tener una alerta en el PCM cuando se dan
// estas solicitudes, dado que tengo que navegar hasta el final de la
// página para ver ADMIN/Consultas agendadas").
//
// El aviso por Telegram (services/telegram_alerts.py) es el golpecito en
// el hombro del momento; esto es el recordatorio que se queda adentro del
// producto hasta que la solicitud se resuelve. Son complementarios: una
// notificación se pierde entre otras veinte, un número en rojo en la barra
// no.
//
// Dos decisiones deliberadas:
//
// - **Solo se consulta si el usuario es admin.** Para cualquier otro
//   usuario el endpoint devuelve 403, así que ni se pregunta: no tiene
//   sentido gastar una llamada por minuto por cada cliente conectado.
// - **Falla en silencio.** Si el backend está caído o el token venció,
//   esto no muestra error ni rompe la barra lateral -- es un adorno
//   informativo, no una función del producto.

const INTERVALO_MS = 60000;

export const EVENTO_ACTUALIZAR = 'pcm:consultas-actualizadas';

/** Avisa a los contadores que algo cambió, sin esperar al próximo ciclo. */
export const avisarConsultasActualizadas = () => {
  window.dispatchEvent(new Event(EVENTO_ACTUALIZAR));
};

export const useConsultasPendientes = () => {
  const { user } = useAuth();
  const [pendientes, setPendientes] = useState(0);

  const esAdmin = esCuentaAdmin(user);

  const refrescar = useCallback(() => {
    if (!esAdmin) return;
    apiClient
      .get('/consultations/admin/pending-count')
      .then((res) => setPendientes(res.data?.pendientes || 0))
      .catch(() => {
        /* silencio deliberado: ver comentario de arriba */
      });
  }, [esAdmin]);

  useEffect(() => {
    if (!esAdmin) {
      setPendientes(0);
      return undefined;
    }
    refrescar();
    const id = setInterval(refrescar, INTERVALO_MS);
    // Volver a la pestaña es el momento más probable de haber recibido algo
    // nuevo: refrescar ahí evita esperar hasta un minuto para enterarse.
    const alVolver = () => {
      if (document.visibilityState === 'visible') refrescar();
    };
    document.addEventListener('visibilitychange', alVolver);
    // Cuando Augusto confirma o rechaza en ConsultasPage, el contador tiene
    // que bajar en el acto y no al minuto siguiente: un badge que sigue en
    // rojo después de responder enseña a ignorarlo. Se usa un evento del
    // navegador y no estado compartido porque el contador lo consumen dos
    // componentes que no se conocen entre sí (barra lateral y header).
    window.addEventListener(EVENTO_ACTUALIZAR, refrescar);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener(EVENTO_ACTUALIZAR, refrescar);
    };
  }, [esAdmin, refrescar]);

  return { pendientes, esAdmin, refrescar };
};

export default useConsultasPendientes;
